import { FC, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'classnames';

import { useGetProjectsListQuery, useGetSharedProjectsListQuery } from 'services';
import { TableSelection, useTableSelection } from 'features/table';
import { CreateProjectButton, SidebarMenu, ProjectRow } from 'features/projects';
import { Project } from 'shared/generated-models';
import { Icon, Text } from 'shared/components/ui';

import styles from './index.module.scss';

interface Props {
	className?: string;
}

export const ProjectsSidebar: FC<Props> = ({ className }: Props) => {
	const { data } = useGetProjectsListQuery();
	const { data: shared } = useGetSharedProjectsListQuery();
	const { id: projectId } = useParams();
	const hasScrolled = useRef(false);

	const combinedProjects = [...(data?.projects || []), ...(shared?.projects || [])];
	const combinedCount = (data?.projects_count || 0) + (shared?.projects_count || 0);

	const { isAllSelected, toggle, isSelected, count, togglePage, includedItems, someSelected, clearSelection, mode } =
		useTableSelection<Project>({
			data: combinedProjects,
			totalCount: combinedCount
		});

	useEffect(() => {
		if (!projectId || hasScrolled.current) return;
		const attemptScroll = () => {
			const el = document.getElementById(`project-${projectId}`);
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				return true;
			}
			return false;
		};
		if (attemptScroll()) {
			hasScrolled.current = true;
			return;
		}
		const id = setInterval(() => {
			if (attemptScroll()) {
				hasScrolled.current = true;
				clearInterval(id);
			}
		}, 100);

		return () => clearInterval(id);
	}, [projectId, combinedProjects.length]);

	return (
		<div className={cn(styles.container, className)}>
			<div className={styles.header}>
				<TableSelection
					className={styles.headerSelection}
					controls={{ main: { togglePage, isAllSelected, count } }}
					hint="Select projects"
				/>
				<div>
					<CreateProjectButton className={styles.create}>
						<Icon icon="add-folder" />
					</CreateProjectButton>
					<SidebarMenu disabled={!someSelected} projects={includedItems} onDelete={clearSelection} />
				</div>
			</div>
			<div className={styles.list}>
				{data && data.projects_count > 0 && (
					<>
						<div className={styles.subheader}>
							<Icon icon="folder-full" size={20} />
							<Text variant="inter/14/semi">MY PROJECTS</Text>
						</div>
						<div className={styles.projects}>
							{data?.projects.map(project => (
								<ProjectRow
									checkbox={{
										checked: isSelected({ row: project }),
										onChange: () => toggle({ row: project }),
										mode
									}}
									project={project}
									key={project.id}
								/>
							))}
						</div>
					</>
				)}
				{shared && shared.projects_count > 0 && (
					<>
						<div className={cn(styles.subheader, styles.subheaderShared)}>
							<Icon icon="users" size={20} />
							<Text variant="inter/14/semi">SHARED WITH ME</Text>
						</div>
						<div className={styles.projects}>
							{shared?.projects.map(project => (
								<ProjectRow
									checkbox={{
										checked: isSelected({ row: project }),
										onChange: () => toggle({ row: project }),
										mode
									}}
									project={project}
									key={project.id}
									shared
								/>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	);
};
